import { FormEventHandler, ReactElement, useState } from 'react';
import { Device } from '@/api';
import {
    UIFormInput,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISwitch,
    UIText,
    UITextButton
} from '@/ui';
import { useUpdateDeviceFallAlertAction } from '../actions';
import { useDevicesTranslation } from '../hooks';
import { PendingConfigNote } from './pending-config-note';

const DEFAULT_SENSITIVITY = '4';

export type FallAlertModalProps = {
    device: Device;
    isOpen: boolean;
    onClose: () => void;
};

export function FallAlertModal(props: FallAlertModalProps): ReactElement {
    const { isOpen, onClose } = props;
    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen ? <FormContent {...props} /> : null}
        </UIModal>
    );
}

function FormContent(props: FallAlertModalProps): ReactElement {
    const { device, onClose } = props;
    const { t } = useDevicesTranslation();
    const { updateDeviceFallAlert, loading } = useUpdateDeviceFallAlertAction({
        deviceId: device.id
    });

    const fall = device.config?.fallAlert;
    const [enabled, setEnabled] = useState(fall?.enabled ?? false);
    const [sensitivity, setSensitivity] = useState(
        String(fall?.sensitivity ?? DEFAULT_SENSITIVITY)
    );
    const [showErrors, setShowErrors] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    function sensitivityError(): string {
        const value = Number(sensitivity);
        if (!Number.isInteger(value) || value < 1 || value > 9) {
            return t('shared.fields.fall_sensitivity.error_invalid');
        }
        return '';
    }

    function handleResetDefaults() {
        setSensitivity(DEFAULT_SENSITIVITY);
        setShowErrors(false);
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();
        setShowErrors(true);

        if (enabled && sensitivityError()) {
            setShowAdvanced(true);
            return;
        }

        // No change from the current config — don't queue a pointless command.
        const unchanged =
            enabled === (fall?.enabled ?? false) &&
            (!enabled || Number(sensitivity) === fall?.sensitivity);
        if (unchanged) {
            onClose();
            return;
        }

        updateDeviceFallAlert(
            enabled
                ? { enabled: true, sensitivity: Number(sensitivity) }
                : { enabled: false }
        )
            .then(() => onClose())
            .catch(() => {}); // Error handled in the action
    };

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UIModalHeader title={t('components.fall_alert_modal.title')} />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <PendingConfigNote />

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>{t('shared.devices.fall_label')}</UIText>
                            <UIText type="default2">
                                {t(
                                    'components.fall_alert_modal.enabled_description'
                                )}
                            </UIText>
                        </div>
                        <UISwitch checked={enabled} onChange={setEnabled} />
                    </div>

                    {enabled && (
                        <>
                            <div style={{ alignSelf: 'flex-start' }}>
                                <UITextButton
                                    type="button"
                                    variant="primary"
                                    leftIcon={
                                        showAdvanced
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    }
                                    onClick={() =>
                                        setShowAdvanced((show) => !show)
                                    }
                                >
                                    {t('shared.labels.advanced_settings')}
                                </UITextButton>
                            </div>
                            {showAdvanced && (
                                <UIFormInput
                                    label={t(
                                        'shared.devices.fall_sensitivity_label'
                                    )}
                                    description={t(
                                        'shared.devices.fall_sensitivity_description'
                                    )}
                                    type="number"
                                    value={sensitivity}
                                    onChange={setSensitivity}
                                    errorText={
                                        showErrors ? sensitivityError() : ''
                                    }
                                />
                            )}
                        </>
                    )}
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: t('shared.labels.submit')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        disabled: loading,
                        onClick: onClose
                    },
                    ...(enabled && showAdvanced
                        ? [
                              {
                                  variant: 'textDefault' as const,
                                  type: 'button' as const,
                                  text: t('shared.labels.revert_default'),
                                  disabled: loading,
                                  onClick: handleResetDefaults
                              }
                          ]
                        : [])
                ]}
            />
        </form>
    );
}
